import { api } from '../client';

// ==========================================
// Types
// ==========================================

export type PoMilestoneType =
  | 'PO_CREATED'
  | 'PO_SUBMITTED'
  | 'PO_APPROVED'
  | 'PO_SENT_TO_SUPPLIER'
  | 'SUPPLIER_ACKNOWLEDGED'
  | 'PRODUCTION_STARTED'
  | 'PRODUCTION_COMPLETED'
  | 'QUALITY_CHECK'
  | 'READY_FOR_SHIPMENT'
  | 'SHIPPED'
  | 'IN_TRANSIT'
  | 'CUSTOMS_CLEARANCE'
  | 'DELIVERED'
  | 'RECEIVED'
  | 'INVOICED'
  | 'PAID';

export type PoMilestoneStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'SKIPPED' | 'DELAYED';

export interface PoMilestone {
  id: string;
  purchaseOrderId: string;
  type: PoMilestoneType;
  status: PoMilestoneStatus;
  plannedDate: string | null;
  actualDate: string | null;
  location: string | null;
  notes: string | null;
  updatedById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PoWithVisibility {
  id: string;
  poNumber: string;
  supplierId: string;
  supplier: {
    id: string;
    name: string;
    email: string;
  };
  status: string;
  totalAmount: number;
  expectedDate: string | null;
  orderDate: string;
  itemCount: number;
  milestones: PoMilestone[];
  currentMilestone: PoMilestoneType | null;
  progress: number;
  createdAt: string;
  updatedAt: string;
}

export interface PoSupplierPerformance {
  id: string;
  companyId: string;
  supplierId: string;
  supplier: {
    id: string;
    name: string;
    email: string;
  };
  periodMonth: string;
  totalOrders: number;
  onTimeDeliveries: number;
  lateDeliveries: number;
  qualityIssues: number;
  avgLeadTimeDays: number | null;
  avgLeadTimeVariance: number | null;
  fulfillmentRate: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeadTimeAnalysis {
  id: string;
  companyId: string;
  supplierId: string;
  supplier: {
    id: string;
    name: string;
  };
  itemId: string | null;
  item: {
    id: string;
    name: string;
    sku: string;
  } | null;
  category: string | null;
  avgLeadTimeDays: number;
  minLeadTimeDays: number;
  maxLeadTimeDays: number;
  stdDeviation: number | null;
  sampleSize: number;
  lastCalculatedAt: string;
}

export interface PoVisibilityDashboard {
  kpis: {
    totalActivePOs: number;
    pendingMilestones: number;
    delayedMilestones: number;
    avgOnTimeRate: number | null;
  };
  recentPOs: {
    id: string;
    poNumber: string;
    supplier: { id: string; name: string };
    status: string;
    totalAmount: number;
    expectedDate: string | null;
    itemCount: number;
    latestMilestone: PoMilestone | null;
  }[];
  topSuppliers: PoSupplierPerformance[];
}

// ==========================================
// DTOs
// ==========================================

export interface CreateMilestoneDto {
  type: PoMilestoneType;
  status?: PoMilestoneStatus;
  plannedDate?: string;
  actualDate?: string;
  location?: string;
  notes?: string;
}

export interface UpdateMilestoneDto {
  status?: PoMilestoneStatus;
  plannedDate?: string;
  actualDate?: string;
  location?: string;
  notes?: string;
}

export interface QueryVisibilityParams {
  supplierId?: string;
  search?: string;
  milestoneStatus?: PoMilestoneStatus;
  expectedDateFrom?: string;
  expectedDateTo?: string;
  page?: number;
  limit?: number;
}

export interface QueryPoSupplierPerformanceParams {
  supplierId?: string;
  startMonth?: string;
  endMonth?: string;
  limit?: number;
}

export interface QueryLeadTimeParams {
  supplierId?: string;
  itemId?: string;
  category?: string;
}

// ==========================================
// Response Types
// ==========================================

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface PoSupplierPerformanceResponse {
  records: PoSupplierPerformance[];
  summary: {
    totalOrders: number;
    onTimeDeliveries: number;
    lateDeliveries: number;
    qualityIssues: number;
    onTimeRate: number | null;
  };
}

// ==========================================
// API Functions
// ==========================================

export const poVisibilityApi = {
  // Dashboard
  getDashboard: () => api.get<PoVisibilityDashboard>('/api/po-visibility/dashboard'),

  // PO Visibility List
  getAll: (params?: QueryVisibilityParams) =>
    api.get<PaginatedResponse<PoWithVisibility>>('/api/po-visibility', { params }),

  // PO Timeline
  getPoTimeline: (poId: string) => api.get<PoWithVisibility>(`/api/po-visibility/${poId}`),

  // Milestones
  getMilestones: (poId: string) => api.get<PoMilestone[]>(`/api/po-visibility/${poId}/milestones`),

  addMilestone: (poId: string, data: CreateMilestoneDto) =>
    api.post<PoMilestone>(`/api/po-visibility/${poId}/milestones`, data),

  updateMilestone: (milestoneId: string, data: UpdateMilestoneDto) =>
    api.patch<PoMilestone>(`/api/po-visibility/milestones/${milestoneId}`, data),

  deleteMilestone: (milestoneId: string) =>
    api.delete<{ success: boolean; message: string }>(`/api/po-visibility/milestones/${milestoneId}`),

  // Supplier Performance
  getPoSupplierPerformance: (params?: QueryPoSupplierPerformanceParams) =>
    api.get<PoSupplierPerformanceResponse>('/api/po-visibility/supplier-performance', { params }),

  calculatePoSupplierPerformance: (supplierId: string, month: string) =>
    api.post<PoSupplierPerformance>('/api/po-visibility/supplier-performance/calculate', {
      supplierId,
      month,
    }),

  // Lead Time Analysis
  getLeadTimeAnalysis: (params?: QueryLeadTimeParams) =>
    api.get<LeadTimeAnalysis[]>('/api/po-visibility/lead-time-analysis', { params }),

  calculateLeadTime: (supplierId: string, itemId?: string) =>
    api.post<LeadTimeAnalysis>('/api/po-visibility/lead-time-analysis/calculate', {
      supplierId,
      itemId,
    }),

  // PO-Shipment Links
  linkShipment: (poId: string, shipmentId: string) =>
    api.post(`/api/po-visibility/${poId}/shipments/${shipmentId}`),

  unlinkShipment: (poId: string, shipmentId: string) =>
    api.delete<{ success: boolean; message: string }>(
      `/api/po-visibility/${poId}/shipments/${shipmentId}`,
    ),
};

// ==========================================
// Milestone Type Labels
// ==========================================

export const milestoneTypeLabels: Record<PoMilestoneType, string> = {
  PO_CREATED: 'PO Created',
  PO_SUBMITTED: 'PO Submitted',
  PO_APPROVED: 'PO Approved',
  PO_SENT_TO_SUPPLIER: 'Sent to Supplier',
  SUPPLIER_ACKNOWLEDGED: 'Supplier Acknowledged',
  PRODUCTION_STARTED: 'Production Started',
  PRODUCTION_COMPLETED: 'Production Completed',
  QUALITY_CHECK: 'Quality Check',
  READY_FOR_SHIPMENT: 'Ready for Shipment',
  SHIPPED: 'Shipped',
  IN_TRANSIT: 'In Transit',
  CUSTOMS_CLEARANCE: 'Customs Clearance',
  DELIVERED: 'Delivered',
  RECEIVED: 'Received',
  INVOICED: 'Invoiced',
  PAID: 'Paid',
};

export const milestoneStatusColors: Record<PoMilestoneStatus, string> = {
  PENDING: 'bg-gray-100 text-gray-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700',
  SKIPPED: 'bg-slate-100 text-slate-700',
  DELAYED: 'bg-red-100 text-red-700',
};
